import React, { useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import loadable from '@loadable/component'

import breadcrumbSchema, { contentfulSchema } from '../../../services/jsonld/jsonldSchema'
import PageTemplate from '../../templates/PageTemplate'
import componentTemplate from './componentTemplate'
import articleSchemaTemplate from '../../../services/jsonld/articleSchemaTemplate'
import Footer from '../../../containers/Footer/index'
import { venture, baseUrl, homePageUrl } from '../../../config'
import ComponentSelector from '../../ComponentSelector'
import { getDefaultMetaDescription } from '../../../utils'
import { constructHowToSchema } from '../../../services/jsonld/howToSchema'

const DynamicHeader = loadable(() =>
  import('@albatross/common-components/dist/components/DynamicHeader')
)
const HTMLHeaderElements = loadable(() => import('@albatross/common-components/dist/components/MetaTags'))
const NavigationBreadcrumb = loadable(() => import('@albatross/common-components/dist/components/NavigationBreadcrumb'))

const BlogPage = ({
  post,
  posts,
  globalContent,
  globalComponents,
  enableContentful,
  path
}) => {
  const [, toggleUpdate] = useState(false)
  const componentConfig = componentTemplate(post, posts)
  const url = `${baseUrl}${homePageUrl}${post.categoryId}/${post.subCategoryId}/${post.id}`

  const categories = enableContentful
    ? [
        {
          slug: post.categoryId,
          name: post.categoryName || post.categoryId
        },
        {
          slug: post.subCategoryId,
          name: post.subCategoryName || post.subCategoryId
        }
      ]
    : globalContent.categories

  const schemaValues = {
    url,
    base: baseUrl,
    gameTitle: post.title,
    categories
  }

  const contentfulJsonLd = contentfulSchema(post.jsonld)

  const jsonldArray = enableContentful
    ? contentfulJsonLd
    : [
        post?.jsonld,
        articleSchemaTemplate(post),
        breadcrumbSchema(schemaValues)
      ].filter((x) => x)

  if (post.jsonldHowTo?.hasJsonldHowTo) {
    jsonldArray.push(constructHowToSchema(post.jsonldHowTo))
  }

  useEffect(() => {
    toggleUpdate(true)
  }, [])

  const hasSubNav = !!componentConfig.filter(component => component.type === 'NavigationMenu' || component.type === 'UnicornNavigationMenu').length

  return (
    <PageTemplate
      components={componentConfig}
      globalContent={globalContent}
    >
      <HTMLHeaderElements
        canonical={post.canonical || url}
        title={post.metaTitle}
        metaTags={[
          {
            name: 'description',
            content:
              post.metaDescription ||
              getDefaultMetaDescription(post.introduction)
          }
        ]}
        jsonld={jsonldArray}
        headerLang={globalContent.headerLang}
        currentPathname={path}
      />
        <DynamicHeader
          venture={venture}
          globalConfig={globalComponents?.components?.DynamicHeader?.globalConfig}
          pageConfig={post?.overrideComponents?.header}
          categories={globalContent.menuItems}
          appLink={globalContent.appLinks}
          hasSubNav={hasSubNav}
          enableContentful={enableContentful}
        />
      <NavigationBreadcrumb showBreadcrumb={globalContent.showBreadcrumb} venture={venture} />
      <ComponentSelector
        {...{ components: componentConfig, post, globalContent, enableContentful }}
      />
      <Footer
        venture={venture}
        pageFooterOverride={post.pageFooterOverride}
        enableContentful={enableContentful}
        customTermsAndConditions={post?.welcomeBonusTermsAndConditionsLong}
      />
    </PageTemplate>
  )
}

BlogPage.propTypes = {
  post: PropTypes.object,
  posts: PropTypes.array,
  globalContent: PropTypes.object,
  globalComponents: PropTypes.object,
  enableContentful: PropTypes.bool,
  path: PropTypes.string
}

export default BlogPage
